import { useState, type MouseEvent, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SpaceScopeSpaceOverride } from '@/contexts/SpaceScopeContext';
import { SpacePartnerPreviewContext, useSpacePartnerPreview } from '@/hooks/useSpacePartnerPreview';
import { participantName } from '@/lib/spaces/settlement';
import { track } from '@/lib/telemetry';
import { SettlementAvatar } from './SettlementParts';
import { cn } from '@/lib/utils';
import type { SpaceDetail } from '@/types/spaces';

// Wraps every page of one space. The preview ends when the person it imitates is no longer in the roster,
// and the parent remounts this per space, so it never leaks into another space.
export function SpacePartnerPreviewScope({ space, children }: Readonly<{ space: SpaceDetail; children: ReactNode }>) {
  const [requested, setRequested] = useState<string | null>(null);
  // This wraps every page of the space, so it must never be the reason one fails to render: without a roster
  // (a partial or older space body) there is simply nobody to preview as.
  const members = Array.isArray(space.members) ? space.members : [];
  const viewerId = requested && members.some((member) => member.userId === requested) ? requested : null;
  // Forget the request once that person is gone: if they came back later, a preview nobody asked for again
  // would silently resume.
  if (requested && !viewerId) setRequested(null);
  // Ruled 2026-09-20: a preview is about what the other person SEES, so the viewer's own actions are put away
  // while it lasts (it is not a "view as": nothing of the other person's is offered either). Every action in a
  // space is gated on `myRole === 'editor'`, so the pages below are simply handed the space as a read-only one:
  // one seam instead of a check at every button, and a future action is covered without remembering to.
  // `useSpaceActionsHidden` lets the few "somente leitura" notices say why instead.
  const shown = viewerId ? { ...space, myRole: 'viewer' as const } : space;
  const actionsHidden = viewerId !== null && space.myRole === 'editor' && !space.archivedAt;
  return <SpacePartnerPreviewContext.Provider value={{ viewerId, actionsHidden, preview: setRequested }}>
    <PartnerPreviewBanner space={space} />
    <SpaceScopeSpaceOverride space={shown}><div data-partner-preview={viewerId ? '' : undefined}>{children}</div></SpaceScopeSpaceOverride>
  </SpacePartnerPreviewContext.Provider>;
}

function PartnerPreviewBanner({ space }: Readonly<{ space: SpaceDetail }>) {
  const { viewerId, actionsHidden, preview } = useSpacePartnerPreview();
  const handleExit = () => preview(null);
  const viewer = viewerId ? space.members.find((member) => member.userId === viewerId) : undefined;
  if (!viewer) return null;
  const name = participantName(space, viewer.userId);
  return <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-info-border bg-info-surface px-4 py-3 text-xs text-info-text">
    <EyeIcon aria-hidden weight="bold" className="size-4 shrink-0" />
    <SettlementAvatar name={name} photoUrl={viewer.photoUrl} className="size-6 text-2xs" />
    <p className="min-w-0 flex-1 basis-48 break-words leading-relaxed"><strong className="font-semibold">Prévia do que {name} vê{viewer.role === 'viewer' ? ' (somente leitura)' : ''}.</strong> O que está borrado é seu e nunca entrou no espaço: a conta de origem, o valor total de uma despesa parcial e a sua regra de envio.<ActionsNote visible={actionsHidden} />
      <ViewerNote visible={viewer.role === 'viewer'} name={name} /></p>
    <Button variant="outline" size="sm" onClick={handleExit}>Sair da prévia</Button>
  </div>;
}

// Only promised when leaving the preview really brings actions back: not in a closed space, nor for a viewer.
function ActionsNote({ visible }: Readonly<{ visible: boolean }>) {
  if (!visible) return null;
  return <> As suas ações ficam guardadas até você sair da prévia.</>;
}

function ViewerNote({ visible, name }: Readonly<{ visible: boolean; name: string }>) {
  if (!visible) return null;
  return <> {name} vê tudo isto, mas não envia despesas nem altera o espaço.</>;
}

// A payer-only value (source account, original total, own rule value). During a preview it is blurred rather
// than removed, so the person sees WHERE their private details sit, and it leaves the accessibility tree with a
// spoken placeholder in its place. The masking lives here, not in CSS on the wrapper, because drawers render in
// a portal outside that wrapper while React context still reaches them.
export function SpacePrivate({ children, className }: Readonly<{ children: ReactNode; className?: string }>) {
  const { viewerId } = useSpacePartnerPreview();
  if (!viewerId) return <span data-private className={className}>{children}</span>;
  return <><span data-private aria-hidden className={cn(className, 'select-none blur-sm')}>{children}</span><span className="sr-only">oculto na prévia</span></>;
}

// One button per other member; hidden when the space has nobody else to imitate.
export function SpacePartnerPreviewToggle({ space }: Readonly<{ space: SpaceDetail }>) {
  const { userId } = useAuth();
  const { viewerId, preview } = useSpacePartnerPreview();
  const others = (Array.isArray(space.members) ? space.members : []).filter((member) => member.userId !== userId);
  const handlePreview = (event: MouseEvent<HTMLButtonElement>) => {
    const opening = event.currentTarget.value !== viewerId;
    // Whose view was opened is nobody's business but theirs: the event carries no member.
    if (opening) track('space.partner_preview.opened');
    preview(opening ? event.currentTarget.value : null);
  };
  const renderMember = (member: SpaceDetail['members'][number]) => <button key={member.userId} type="button" value={member.userId} aria-pressed={viewerId === member.userId} onClick={handlePreview}
    className={cn('flex max-w-48 cursor-pointer items-center gap-1.5 border-l border-border-subtle px-3 py-2 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring focus-visible:outline-none',
      viewerId === member.userId ? 'bg-info-surface text-info-text' : 'text-text-secondary hover:bg-surface-sunken')}><SettlementAvatar name={participantName(space, member.userId)} photoUrl={member.photoUrl} className="size-5 text-2xs" /><span className="min-w-0 truncate">{participantName(space, member.userId)}</span></button>;
  if (!others.length) return null;
  return <div role="group" aria-label="Ver o espaço como outra pessoa vê" className="flex max-w-full flex-nowrap items-stretch overflow-x-auto rounded-2xl border border-border-subtle bg-surface">
    <span className="flex shrink-0 items-center gap-1 py-2 pl-3 pr-2 text-xs font-semibold text-text-secondary"><EyeSlashIcon aria-hidden weight="bold" className="size-4" />Ver como</span>
    {others.map(renderMember)}
  </div>;
}
